import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { changeStaffPictureProfile, getStaffDetails, removeStaffAssignment } from '../services/staff.service';

/**
 * Hook para obtener los detalles de un miembro específico del staff
 * @param {Object} params - Parámetros del hook
 * @param {string} params.supplierId - ID del supplier
 * @param {string} params.staffAssignmentId - ID del staff assignment
 * @returns {Object} - Resultado de la query con data, loading, error, etc.
 */
export const useStaffDetails = ({ supplier, staffAssignmentId, removeSelectedStaff }) => {
    const queryClient = useQueryClient();
    
    const [staffModal, setStaffModal] = useState(false);
    
    const queryKey = ['staffDetails', supplier._id, staffAssignmentId];

    const {
        data,
        isError,
        error,
        isLoading,
        refetch,
    } = useQuery({
        queryKey,
        queryFn: () => getStaffDetails({ staffAssignmentId }),
        enabled: !!supplier && !!staffAssignmentId,
        staleTime: 5 * 60 * 1000, // 5 minutos
        cacheTime: 10 * 60 * 1000, // 10 minutos
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        onError: (error) => {
            console.error('Error fetching staff details:', error.response?.data?.message || error.message || 'Error getting staff details');
        },
    });

    useEffect(() => {
        if (!isLoading && error) {
            Swal.fire({
                icon: 'error',
                title: 'Error loading staff details',
                text: error.response?.data?.message || error.message || 'An unexpected error occurred',
                showConfirmButton: true,
                confirmButtonText: 'OK',
            });

            removeSelectedStaff?.()

            return
        }
        
        if (isLoading) {
            Swal.fire({
                footer: 'Loading, please wait',
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });

            return
        }

        if (!isLoading && data) {
            setStaffModal(true)
            Swal.close()
        }
    }, [isLoading, error, data, removeSelectedStaff]);

    const changeStaffPictureProfileMutation = useMutation({
        mutationFn: ({ staffId, file, staffNSS }) => 
            changeStaffPictureProfile({ staffId, file, staffNSS }),
        onSuccess: () => {
            // Invalidar las queries relacionadas
            queryClient.invalidateQueries(queryKey);
        },
        onError: (error) => {
            Swal.fire({
				icon: 'error',
				text: error.response?.data?.message || error.message || 'An error occurred while changing the profile picture.',
				showConfirmButton: false,
				timer: 5000,
				timerProgressBar: true,
			})
            
            console.error('Error changing staff profile picture:', error.response?.data?.message || error.message);
        }
    })

    const removeStaffAssignmentMutation = useMutation({
        mutationFn: ({ assignmentId }) => 
            removeStaffAssignment({ assignmentId }),
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey });
        },
        onSuccess: () => {
            // Invalidar las queries relacionadas
            queryClient.invalidateQueries(queryKey);
            queryClient.invalidateQueries({
                queryKey: ['staffList']
            });
        },
        onError: (error) => {
            Swal.fire({
                icon: 'error',
                text: error.response?.data?.message || error.message || 'An error occurred while removing the staff assignment.',
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            })
            console.error('Error removing staff assignment:', error.response?.data?.message || error.message);
        }
    })

    const handleCloseModal = () => {
        setStaffModal(false);
    }

    const changeStaffPicture = ({ staffId, file, staffNSS }) => {
        changeStaffPictureProfileMutation.mutate({ staffId, file, staffNSS });
    }

    const removeStaffAssignmentHandler = async ({ assignmentId }) => {
        try {
            await removeStaffAssignmentMutation.mutateAsync({ assignmentId });
        } catch {
            return false
        }
    }

    const dataToReturn = {
        data,
        isError,
        error,
        isLoading,
        refetch,

        // states
        staffModal,

        // handlers
        handleCloseModal,
        changeStaffPicture,
        removeStaffAssignmentHandler,
        isDeletingStaffAssignment: removeStaffAssignmentMutation.isPending,
    }

    return dataToReturn;
};

export default useStaffDetails;
